import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { catalogBaselineUrl, getProjectCatalog, previewProjectCatalog, type QaCatalog, type QaCatalogCheck, type QaCatalogPlan, type QaCatalogPreview, type QaCatalogStep, type QaCatalogTest } from "../api/qaOrchestrator";
import { describeStep, regionAspect, regionLabel } from "./qaCatalogSteps";
import "./admin/qa-orchestrator.css";
import "./admin/qa-run.css";
import "./admin/qa-catalog.css";

type Props = { project: string };

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Does the test mention every word of the query (in its plan, id, texts or steps)? */
export function testMatches(plan: QaCatalogPlan, test: QaCatalogTest, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = [plan.name, test.id, test.description, test.expected, test.failureLooksLike, ...test.steps.map((s) => s.summary), ...test.images.map((i) => i.name)].join("\n").toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** "gamemode creative · spawn 10 64 -3" */
export function setupLabel(setup: QaCatalogTest["setup"]): string | null {
  if (!setup) return null;
  const parts = [setup.gamemode, setup.spawn ? `spawn ${setup.spawn.x} ${setup.spawn.y} ${setup.spawn.z}` : null].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

/** The reference images a test names that aren't there, and its image checks that name none. */
export function missingReferences(test: QaCatalogTest): string[] {
  const unnamed = test.steps.flatMap((s) => s.checks ?? []).filter((c) => c.type === "image" && !c.baseline).map((c) => `${c.id} (no reference named)`);
  return [...test.images.filter((i) => !i.exists).map((i) => i.name), ...unnamed];
}

/** A file of a picked or dropped folder, at its path from that folder ("backpacks/qa/plans/x.plan.yaml"). */
export type PickedFile = { path: string; file: File };

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

// Where a qa "plans" or "baselines" folder is in a path: under qa/, or the
// picked folder itself or its child.
const qaDir = (segs: string[], dir: string) => segs.findIndex((s, i) => s === dir && (i <= 1 || segs[i - 1] === "qa"));

/**
 * The plans and baselines of a picked folder (the pack repo, its qa folder or
 * the plans folder), at their paths in the repo: qa/plans/<name>.plan.yaml
 * and qa/baselines/<name>.png.
 */
export function packFiles(picked: PickedFile[]): { folder: string; plans: Map<string, File>; images: Map<string, File> } {
  const files = picked.map(({ path, file }) => ({ segs: path.split("/").filter(Boolean), file })).filter(({ segs }) => segs.length > 0);
  const isPlan = (segs: string[]) => segs.at(-1)!.endsWith(".plan.yaml");
  const inPlans = (segs: string[]) => isPlan(segs) && qaDir(segs, "plans") === segs.length - 2;
  // The plans of a plans folder; without one, the plan files the picked folder holds itself.
  const anyInPlans = files.some(({ segs }) => inPlans(segs));
  const plans = new Map<string, File>();
  const images = new Map<string, File>();
  for (const { segs, file } of files) {
    const name = segs.at(-1)!;
    if (isPlan(segs) && (anyInPlans ? inPlans(segs) : segs.length <= 2)) {
      if (!plans.has(`qa/plans/${name}`)) plans.set(`qa/plans/${name}`, file);
    } else if (name.toLowerCase().endsWith(".png")) {
      const at = qaDir(segs, "baselines");
      if (at >= 0 && at < segs.length - 1) images.set(`qa/baselines/${segs.slice(at + 1).join("/")}`, file);
    }
  }
  const first = files[0]?.segs;
  return { folder: first && first.length > 1 ? first[0]! : "", plans, images };
}

/** The plan and image files of dropped folders, read from their entries. */
async function droppedFiles(entries: FileSystemEntry[]): Promise<PickedFile[]> {
  const out: PickedFile[] = [];
  const walk = async (entry: FileSystemEntry, path: string): Promise<void> => {
    if (entry.isFile) {
      if (/\.(plan\.yaml|png)$/i.test(entry.name)) out.push({ path, file: await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej)) });
      return;
    }
    if (!entry.isDirectory || entry.name === "node_modules" || entry.name.startsWith(".")) return;
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
      if (batch.length === 0) return;
      for (const e of batch) await walk(e, `${path}/${e.name}`);
    }
  };
  for (const e of entries) await walk(e, e.name);
  return out;
}

/** A baseline name as an image URL. */
type ImageSource = (name: string) => Promise<string>;

/** An uploaded folder's catalog, and its baselines as object URLs made when first shown. */
type Upload = { folder: string; planFiles: number; imageFiles: number; catalog: QaCatalogPreview; source: ImageSource; urls: string[] };

function uploadSource(images: Map<string, File>, urls: string[]): ImageSource {
  const made = new Map<string, string>();
  return async (name) => {
    let url = made.get(name);
    if (!url) {
      const file = images.get(`qa/baselines/${name}`);
      if (!file) throw new Error(`${name} is not in the upload`);
      url = URL.createObjectURL(file);
      made.set(name, url);
      urls.push(url);
    }
    return url;
  };
}

// Object URLs of baselines already fetched, by project, commit and name: a sha never changes.
const thumbs = new Map<string, Promise<string>>();
function githubBaseline(project: string, sha: string, name: string): Promise<string> {
  const key = `${project}@${sha}/${name}`;
  if (!thumbs.has(key)) thumbs.set(key, catalogBaselineUrl(project, sha, name).catch((e) => { thumbs.delete(key); throw e; }));
  return thumbs.get(key)!;
}

function useBaseline(source: ImageSource, name: string | undefined, exists: boolean) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!name || !exists) return;
    let live = true;
    source(name).then((u) => live && setUrl(u)).catch(() => live && setFailed(true));
    return () => { live = false; };
  }, [source, name, exists]);
  return { url, failed };
}

type OpenImage = (url: string, name: string) => void;

function Thumb({ source, name, kind, exists, aspect, onOpen }: { source: ImageSource; name: string; kind: "reference" | "mask"; exists: boolean; aspect?: number | null; onOpen: OpenImage }) {
  const { url, failed } = useBaseline(source, name, exists);
  const cls = `qc-thumb${kind === "mask" ? " qc-thumb-small" : ""}`;
  // The region's shape, at most about 220px tall.
  const a = aspect ? Math.min(Math.max(aspect, 0.75), 3) : null;
  const style = a && kind === "reference" ? { aspectRatio: String(a), width: `min(100%, ${Math.round(220 * a)}px)` } : undefined;
  if (!exists) return <span className={`${cls} qc-thumb-missing`} style={style} title={`${name} is not in qa/baselines`}>missing</span>;
  if (failed) return <span className={`${cls} qc-thumb-missing`} style={style} title={name}>unavailable</span>;
  if (!url) return <span className={`${cls} qc-thumb-loading`} style={style} aria-label={`Loading ${name}`} />;
  return (
    <button type="button" className={cls} style={style} title={`Enlarge ${name}`} onClick={() => onOpen(url, name)}>
      <img src={url} alt={`${kind} ${name}`} />
    </button>
  );
}

function CheckCard({ source, check, test, onOpen }: { source: ImageSource; check: QaCatalogCheck; test: QaCatalogTest; onOpen: OpenImage }) {
  const exists = (name: string, kind: "baseline" | "mask") => test.images.find((i) => i.name === name && i.kind === kind)?.exists ?? false;
  const text = check.type === "text";
  return (
    <div className={`qc-check${text ? " qc-check-text" : ""}`}>
      {text ? (
        <div className="qc-ocr">
          <span className="qc-ocr-label">Reads text</span>
          <span className="qc-ocr-expect">{check.contains !== undefined ? <>containing <q>{check.contains}</q></> : <>matching <code>/{check.matches}/</code></>}</span>
        </div>
      ) : check.baseline ? (
        <Thumb source={source} name={check.baseline} kind="reference" exists={exists(check.baseline, "baseline")} aspect={regionAspect(check.region)} onOpen={onOpen} />
      ) : (
        <span className="qc-thumb qc-thumb-missing">no reference</span>
      )}
      <dl className="qc-check-facts">
        <dt>Check</dt>
        <dd><code className="qc-check-id">{check.id}</code> <span className="qo-muted">{text ? "text (OCR)" : "image"}</span></dd>
        {!text && <><dt>Reference</dt><dd><code>{check.baseline ?? "none"}</code></dd></>}
        <dt>Region</dt>
        <dd>{regionLabel(check.region)}</dd>
        {check.tolerance !== undefined && <><dt>Tolerance</dt><dd>{check.tolerance}</dd></>}
        {check.mask && (
          <>
            <dt>Mask</dt>
            <dd className="qc-mask"><Thumb source={source} name={check.mask} kind="mask" exists={exists(check.mask, "mask")} onOpen={onOpen} /><code>{check.mask}</code></dd>
          </>
        )}
      </dl>
    </div>
  );
}

function StepRow({ n, step, source, test, onOpen }: { n: number; step: QaCatalogStep; source: ImageSource; test: QaCatalogTest; onOpen: OpenImage }) {
  const w = describeStep(step);
  return (
    <li className={`qc-step${w.quiet ? " qc-step-quiet" : ""}${step.checks ? " qc-step-shot" : ""}`}>
      <span className="qc-step-n">{n}</span>
      <div className="qc-step-body">
        <div className="qc-step-line" title={step.summary}>
          <span className="qc-step-label">{w.label}</span>
          {w.key && <kbd className="qc-key">{w.key}</kbd>}
          {w.code && <code className="qc-step-code">{w.code}</code>}
          {w.detail && <span className="qc-step-detail">{w.detail}</span>}
        </div>
        {w.facts && w.facts.length > 0 && (
          <dl className="qc-step-facts">
            {w.facts.map((f, i) => <div key={i}><dt>{f.name}</dt><dd>{f.value}</dd></div>)}
          </dl>
        )}
        {step.checks && step.checks.length > 0 && (
          <div className="qc-checks">{step.checks.map((c) => <CheckCard key={c.id} source={source} check={c} test={test} onOpen={onOpen} />)}</div>
        )}
      </div>
    </li>
  );
}

function TestDetail({ plan, test, source, onOpen }: { plan: QaCatalogPlan; test: QaCatalogTest; source: ImageSource; onOpen: OpenImage }) {
  const missing = missingReferences(test);
  return (
    <article className="qc-detail" aria-label={`Test ${plan.name}/${test.id}`}>
      <header className="qc-detail-head">
        <div className="qo-muted qc-detail-crumb">{plan.name}</div>
        <h2 className="qc-detail-id">{test.id}</h2>
        <div className="qc-detail-counts">
          <span className="qr-count">{plural(test.stepCount, "step")}</span>
          {test.screenshotChecks > 0 && <span className="qr-count">{plural(test.screenshotChecks, "check")}</span>}
          {missing.length > 0 && <span className="qr-count qc-count-warn">{missing.length} missing</span>}
        </div>
      </header>
      {test.description && <p className="qc-desc">{test.description}</p>}
      {missing.length > 0 && (
        <div className="qc-problem qc-problem-warn" role="note">
          <strong>{missing.length === 1 ? "A reference image is missing" : `${missing.length} reference images are missing`}</strong>
          <span>This test can't pass until {missing.length === 1 ? "it is" : "they are"} in qa/baselines: {missing.map((m, i) => <span key={m}>{i > 0 && ", "}<code>{m}</code></span>)}</span>
        </div>
      )}
      <section className="qc-section">
        <h3>Setup</h3>
        {test.setup && (test.setup.gamemode || test.setup.spawn) ? (
          <dl className="qc-setup">
            {test.setup.gamemode && <div><dt>Game mode</dt><dd>{test.setup.gamemode}</dd></div>}
            {test.setup.spawn && <div><dt>Spawn</dt><dd><code>{test.setup.spawn.x} {test.setup.spawn.y} {test.setup.spawn.z}</code></dd></div>}
          </dl>
        ) : (
          <p className="qo-muted qc-none">None: the world and game mode as the runner has them.</p>
        )}
      </section>
      <div className="qc-outcomes">
        <section className="qc-outcome qc-outcome-ok">
          <h3>Expected</h3>
          <p>{test.expected}</p>
        </section>
        <section className="qc-outcome qc-outcome-bad">
          <h3>Failure looks like</h3>
          <p>{test.failureLooksLike}</p>
        </section>
      </div>
      <section className="qc-section">
        <h3>Steps <span className="qo-muted">{test.steps.length}</span></h3>
        <ol className="qc-steps" aria-label={`Steps of ${plan.name}/${test.id}`}>
          {test.steps.map((s, i) => <StepRow key={i} n={i + 1} step={s} source={source} test={test} onOpen={onOpen} />)}
        </ol>
      </section>
    </article>
  );
}

/** The plan's problems for its badge: missing images, or a plan that doesn't validate. */
function planProblem(plan: QaCatalogPlan): { kind: "missing" | "invalid"; label: string } | null {
  const missing = plan.tests.reduce((n, t) => n + missingReferences(t).length, 0);
  if (plan.issues?.length || (plan.error && missing === 0)) return { kind: "invalid", label: "invalid" };
  if (missing > 0) return { kind: "missing", label: `${missing} missing` };
  return null;
}

// The selected plan and test live in the URL (?plan=&test=), so a reload or a
// shared link opens the same test. Only on real http(s) pages.
const canRoute = () => typeof window !== "undefined" && /^https?:$/.test(window.location.protocol);
type Selection = { plan?: string; test?: string };
function readSelection(): Selection {
  if (!canRoute()) return {};
  const q = new URLSearchParams(window.location.search);
  return { plan: q.get("plan") ?? undefined, test: q.get("test") ?? undefined };
}
function writeSelection(sel: Selection) {
  if (!canRoute()) return;
  const q = new URLSearchParams(window.location.search);
  for (const k of ["plan", "test"] as const) {
    const v = sel[k];
    if (v) q.set(k, v);
    else q.delete(k);
  }
  const search = q.toString() ? `?${q}` : "";
  if (search === window.location.search) return;
  try { window.history.replaceState(window.history.state, "", window.location.pathname + search + window.location.hash); } catch { /* history may be locked down */ }
}

const narrow = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 960px)").matches;

/**
 * Every test the project's repo has in qa/plans at a branch (default: the
 * project's), read through the orchestrator: the plans beside a list of the
 * chosen plan's tests, and the chosen test's setup, outcomes and steps with
 * the reference images its screenshot checks compare against. A folder of
 * plans uploaded from this computer is shown the same way, for a repo the
 * orchestrator can't read; only the YAML and the image names are sent.
 */
export default function QaCatalogView({ project }: Props) {
  const [data, setData] = useState<{ configured: boolean; repo: string; catalog: QaCatalog | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState<Selection>(readSelection);
  const [image, setImage] = useState<{ url: string; name: string } | null>(null);
  const [upload, setUpload] = useState<Upload | null>(null);
  const [dragging, setDragging] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLElement>(null);

  const load = useCallback(async (at?: string) => {
    setLoading(true);
    setError(null);
    try {
      setData(await getProjectCatalog(project, at));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [project]);
  useEffect(() => { void load(); }, [load]);

  // An upload's object URLs go with it: on a new upload, going back, or leaving.
  useEffect(() => () => { for (const url of upload?.urls ?? []) URL.revokeObjectURL(url); }, [upload]);

  const preview = async (picked: PickedFile[]) => {
    const pack = packFiles(picked);
    if (pack.plans.size === 0) {
      setError("No .plan.yaml files there: pick the pack repo, its qa folder or qa/plans.");
      return;
    }
    const bytes = [...pack.plans.values()].reduce((n, f) => n + f.size, 0);
    if (bytes > MAX_UPLOAD_BYTES) {
      setError(`The plans are ${(bytes / 1024 / 1024).toFixed(1)} MB, at most 5 MB.`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const files = Object.fromEntries(await Promise.all([...pack.plans].map(async ([path, f]) => [path, await f.text()] as const)));
      const { catalog } = await previewProjectCatalog(project, files, [...pack.images.keys()]);
      const urls: string[] = [];
      setImage(null);
      setUpload({ folder: pack.folder, planFiles: pack.plans.size, imageFiles: pack.images.size, catalog, source: uploadSource(pack.images, urls), urls });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    // Entries are only readable during the event.
    const entries = [...e.dataTransfer.items].map((i) => i.webkitGetAsEntry?.()).filter((x): x is FileSystemEntry => !!x);
    if (entries.length) void droppedFiles(entries).then(preview, (err) => setError((err as Error).message));
    else void preview([...e.dataTransfer.files].map((file) => ({ path: file.name, file })));
  };

  useEffect(() => {
    if (!image) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setImage(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [image]);

  const fromGitHub = data?.catalog ?? null;
  const catalog: QaCatalog | QaCatalogPreview | null = upload?.catalog ?? fromGitHub;
  const sha = fromGitHub?.sha ?? "";
  const githubSource = useCallback<ImageSource>((name) => githubBaseline(project, sha, name), [project, sha]);
  const source = upload?.source ?? githubSource;
  const filtering = query.trim() !== "";
  const plans = useMemo(
    () => (catalog?.plans ?? []).map((plan) => ({ plan, tests: plan.tests.filter((t) => testMatches(plan, t, query)) })).filter((p) => !filtering || p.tests.length > 0),
    [catalog, query, filtering],
  );
  const shownTests = plans.reduce((n, p) => n + p.tests.length, 0);
  const current = plans.find((p) => p.plan.name === sel.plan) ?? plans[0] ?? null;
  const test = current ? current.tests.find((t) => t.id === sel.test) ?? current.tests[0] ?? null : null;
  const planName = current?.plan.name;
  const testId = test?.id;
  useEffect(() => { if (planName) writeSelection({ plan: planName, test: testId }); }, [planName, testId]);
  // The chosen test stays in sight in its list (one opened from the URL, too).
  useEffect(() => {
    const list = listRef.current;
    const el = list && [...list.querySelectorAll<HTMLElement>("[data-test]")].find((b) => b.dataset.test === testId);
    if (!list || !el) return;
    const top = el.offsetTop;
    if (top < list.scrollTop || top + el.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = Math.max(0, top - list.clientHeight / 3);
  }, [planName, testId, catalog]);

  const pickTest = (id: string, focus = false) => {
    setSel({ plan: planName, test: id });
    if (focus) {
      const el = [...(listRef.current?.querySelectorAll<HTMLElement>("[data-test]") ?? [])].find((b) => b.dataset.test === id);
      el?.focus();
      el?.scrollIntoView?.({ block: "nearest" });
    } else if (narrow()) {
      detailRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    }
  };
  const onListKey = (e: ReactKeyboardEvent) => {
    if (!current || !test) return;
    const at = current.tests.indexOf(test);
    const to = e.key === "ArrowDown" ? at + 1 : e.key === "ArrowUp" ? at - 1 : e.key === "Home" ? 0 : e.key === "End" ? current.tests.length - 1 : null;
    if (to === null) return;
    e.preventDefault();
    const next = current.tests[Math.max(0, Math.min(current.tests.length - 1, to))];
    if (next) pickTest(next.id, true);
  };
  const backToGitHub = () => { setImage(null); setUpload(null); };
  const openImage: OpenImage = (url, name) => setImage({ url, name });

  if (data && !data.configured) return <p className="qo-hint">QA testing is not set up yet. An admin connects it under Manage → QA.</p>;
  return (
    <section
      className={`qc${dragging ? " qc-dragging" : ""}`}
      onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDragging(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false); }}
      onDrop={onDrop}
    >
      <aside className="qc-side">
        <section className="qrp-card qc-source">
          <h2>Source</h2>
          {upload ? (
            <div className="qc-upload" role="note">
              <div>Uploaded folder <code>{upload.folder || "(dropped files)"}</code></div>
              <div className="qo-muted">{plural(upload.planFiles, "plan file")}, {plural(upload.imageFiles, "image")} · not saved, only in this tab</div>
              <button type="button" className="qo-link" onClick={backToGitHub}>Back to {data?.repo || "the repo"}</button>
            </div>
          ) : fromGitHub ? (
            <dl className="qrp-facts qc-repo">
              <dt>Repo</dt><dd><code>{fromGitHub.repo}</code></dd>
              <dt>Commit</dt><dd className="qc-at"><code title={fromGitHub.sha}>{fromGitHub.sha.slice(0, 8)}</code> <span className="qo-muted">on <code>{fromGitHub.ref}</code></span></dd>
            </dl>
          ) : data?.repo ? (
            <dl className="qrp-facts qc-repo"><dt>Repo</dt><dd><code>{data.repo}</code></dd></dl>
          ) : null}
          <form className="qc-ref" onSubmit={(e) => { e.preventDefault(); backToGitHub(); void load(ref.trim() || undefined); }}>
            <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="branch, tag or commit" aria-label="Branch to read" />
            <button className="btn" type="submit" disabled={loading}>{loading ? "Loading…" : "Show"}</button>
          </form>
          <div className="qc-upload-row">
            <button className="btn" type="button" disabled={loading} onClick={() => picker.current?.click()} title="Preview the tests of a folder on this computer: the pack repo, its qa folder or qa/plans.">Upload folder</button>
            <span className="qo-muted">or drop one here</span>
          </div>
          <input
            ref={picker}
            type="file"
            hidden
            multiple
            aria-label="Folder to preview"
            {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            onChange={(e) => {
              const picked = [...(e.target.files ?? [])].map((file) => ({ path: file.webkitRelativePath || file.name, file }));
              e.target.value = "";
              if (picked.length) void preview(picked);
            }}
          />
          {error && <div className="qo-error-text qc-error" role="alert">{error}</div>}
        </section>
        {catalog && (
          <section className="qrp-card qc-plans-card">
            <input className="qc-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter tests…" aria-label="Filter tests" />
            {filtering && <div className="qo-muted qc-filtered">{plural(shownTests, "test")} match</div>}
            <h2>Plans <span className="qo-muted">{catalog.counts.plans}</span></h2>
            <nav className="qrp-plans" aria-label="Plans">
              {plans.map(({ plan, tests }) => {
                const problem = planProblem(plan);
                const on = plan.name === planName;
                return (
                  <button key={plan.name} type="button" className={`qrp-plan qc-plan${on ? " on" : ""}`} aria-current={on ? "true" : undefined} onClick={() => setSel({ plan: plan.name })}>
                    <span className="qrp-plan-name">{plan.name}</span>
                    <span className="qc-plan-count">{filtering ? `${tests.length} of ${plan.tests.length}` : plan.tests.length}</span>
                    {problem && <span className={`qc-badge qc-badge-${problem.kind}`}>{problem.label}</span>}
                  </button>
                );
              })}
              {plans.length === 0 && <div className="qo-muted qc-none">{filtering ? "No test matches." : "No plans in qa/plans."}</div>}
            </nav>
            <div className="qc-totals">
              <span className="qr-count">{plural(catalog.counts.tests, "test")}</span>
              <span className="qr-count">{plural(catalog.counts.screenshotChecks, "screenshot check")}</span>
              {catalog.counts.missingImages > 0 && <span className="qr-count qc-count-warn">{catalog.counts.missingImages} missing image{catalog.counts.missingImages === 1 ? "" : "s"}</span>}
              {catalog.counts.brokenPlans > 0 && <span className="qr-count qr-count-error">{plural(catalog.counts.brokenPlans, "broken plan")}</span>}
            </div>
          </section>
        )}
      </aside>

      <div className="qc-list-pane">
        {!catalog ? (
          !error && <div className="qo-muted qc-empty">Loading tests…</div>
        ) : !current ? (
          <div className="qo-muted qc-empty">{filtering ? `No test matches “${query.trim()}”.` : "No plans in qa/plans at this commit."}</div>
        ) : (
          <>
            <header className="qc-plan-head">
              <strong>{current.plan.name}</strong>
              <span className="qo-muted">{filtering ? `${current.tests.length} of ${plural(current.plan.tests.length, "test")}` : plural(current.plan.tests.length, "test")}</span>
              <code className="qc-plan-file" title={current.plan.file}>{current.plan.file}{current.plan.pack ? ` · pack ${current.plan.pack}` : ""}</code>
            </header>
            {current.plan.error && (
              <div className="qc-problem" role="note">
                <strong>This plan can't run as committed</strong>
                <span className="qc-plan-error">{current.plan.error}</span>
                {current.plan.issues?.length ? <ul>{current.plan.issues.slice(0, 8).map((i, n) => <li key={n}><code>{i.path || "plan"}</code> {i.message}</li>)}</ul> : null}
              </div>
            )}
            <div className="qc-tests" role="listbox" aria-label={`Tests of ${current.plan.name}`} ref={listRef} onKeyDown={onListKey}>
              {current.tests.map((t) => {
                const on = t.id === testId;
                const missing = missingReferences(t).length;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="option"
                    aria-selected={on}
                    tabIndex={on ? 0 : -1}
                    data-test={t.id}
                    className={`qc-test${on ? " on" : ""}`}
                    onClick={() => pickTest(t.id)}
                  >
                    <span className="qc-test-top">
                      <code className="qc-test-id">{t.id}</code>
                      <span className="qc-test-meta">{t.stepCount} steps{t.screenshotChecks > 0 ? ` · ${plural(t.screenshotChecks, "check")}` : ""}</span>
                    </span>
                    {t.description && <span className="qc-test-desc" title={t.description}>{t.description}</span>}
                    {missing > 0 && <span className="qc-test-warn">⚠ {missing === 1 ? "reference missing" : `${missing} references missing`}</span>}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <main className="qc-detail-pane" ref={detailRef}>
        {current && test && <TestDetail key={`${current.plan.name}/${test.id}`} plan={current.plan} test={test} source={source} onOpen={openImage} />}
      </main>

      {image && (
        <div className="qc-lightbox" role="dialog" aria-label={image.name} onClick={() => setImage(null)}>
          <figure>
            <img src={image.url} alt={image.name} />
            <figcaption>{image.name}</figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}
